import { useState, useRef, useEffect } from "react";
import { ArrowDown, Copy, Check, ChevronUp, AlertTriangle, Mic } from "lucide-react";

// ─── Format seconds → MM:SS timestamp ────────────────────────────────────────
function formatTimestamp(seconds) {
  if (seconds == null || seconds < 0) return "";
  const m = Math.floor(seconds / 60).toString().padStart(2, "0");
  const s = Math.floor(seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

// ─── Format utterances for clipboard (matches the displayed layout) ──────────
function formatForCopy(utterances, fallbackText) {
  const cleaned = utterances.filter(u => u.status === "cleaned");
  if (cleaned.length > 0) {
    return cleaned.map(u => {
      const ts = formatTimestamp(u.start_time);
      return ts ? `${ts} ·\n${u.text}` : u.text;
    }).join("\n\n");
  }
  return fallbackText || "";
}

// ─── Single utterance row ─────────────────────────────────────────────────────
function UtteranceItem({ utterance, isEditable, onTextChange }) {
  const isInterim = utterance.status === "interim";

  if (isInterim) {
    return (
      <div
        className="mb-3 text-sm italic text-muted leading-snug"
        style={{ transition: "opacity 0.4s ease", opacity: 0.65 }}
      >
        {utterance.text}
      </div>
    );
  }

  const timestamp = formatTimestamp(utterance.start_time);

  return (
    <div className="mb-3" style={{ transition: "opacity 0.4s ease", opacity: 1 }}>
      {/* Timestamp label row — only shown when we have a non-zero start_time */}
      {timestamp && (
        <div className="flex items-center gap-1 mb-0.5 text-muted">
          <span className="text-[11px] font-bold tracking-widest">
            {timestamp}
          </span>
          <span className="font-black text-sm select-none leading-none"> ·</span>
        </div>
      )}

      {/* Text row — editable after recording stops */}
      <div
        className="text-sm text-ink leading-snug focus:outline-none"
        contentEditable={isEditable}
        suppressContentEditableWarning
        onBlur={e => {
          if (isEditable && onTextChange) {
            onTextChange(utterance.id, e.currentTarget.innerText.trim());
          }
        }}
        style={{ outline: "none" }}
      >
        {utterance.text}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export const TranscriptContainer = ({
  isVisible,
  className,
  isRecording,
  showRecommendation,
  setIsTranscriptEdited,
  transcriptScrollRef,
  editableTranscriptionRef,
  transcribedText,
  recommendationLoading,
  recommendation,
  isTranscriptEdited,
  createSessionLoading,
  loading,
  handleTranscriptUpdate,
  Spinner,
  WarnIcon,
  autoScrollEnabledRef,
  setShowScrollButton,
  showScrollButton,
  TypewriterMarkdown,
  typedRecommendationRef,
  liveUtterances = [],
  onUtteranceTextChange,
  shortTranscriptWarning = false,
}) => {
  const [copied, setCopied] = useState(false);
  const [copyMenuOpen, setCopyMenuOpen] = useState(false);
  const [copyMode, setCopyMode] = useState("timestamps"); // "timestamps" | "no-timestamps"
  const [transcriptFocused, setTranscriptFocused] = useState(false);
  const copyMenuRef = useRef(null);

  /* The transcript as it last came from the session — the thing "has the user
     changed anything?" is measured against.

     It is held in a ref on purpose. The obvious version reads
     `editableTranscriptionRef.current`, but the user's own typing writes to that
     ref, so the baseline would chase the edit and the comparison would always
     come back equal. It re-baselines only when the session's own text changes:
     load, recording stop, or a successful update. */
  const resolveBaseline = () =>
    editableTranscriptionRef.current.length > (transcribedText || "").length
      ? editableTranscriptionRef.current
      : transcribedText || "";

  const baselineRef = useRef(resolveBaseline());

  useEffect(() => {
    baselineRef.current = resolveBaseline();
    setIsTranscriptEdited(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcribedText]);

  const baselineTranscript = baselineRef.current;

  // Close the dropdown when the user clicks outside it
  useEffect(() => {
    const handler = (e) => {
      if (copyMenuRef.current && !copyMenuRef.current.contains(e.target)) {
        setCopyMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const flashCopied = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Default — copy with "MM:SS ·\ntext" format, blocks separated by blank lines
  const handleCopyWithTimestamps = async () => {
    const text = formatForCopy(
      liveUtterances,
      editableTranscriptionRef.current || transcribedText
    );
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      flashCopied();
    } catch { }
    setCopyMode("timestamps");
    setCopyMenuOpen(false);
  };

  // Copy without timestamps — each timestamp block becomes its own paragraph
  const handleCopyWithoutTimestamps = async () => {
    const cleaned = liveUtterances.filter(u => u.status === "cleaned");
    let text;
    if (cleaned.length > 0) {
      text = cleaned.map(u => u.text).join("\n\n");
    } else {
      text = editableTranscriptionRef.current || transcribedText || "";
    }
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      flashCopied();
    } catch { }
    setCopyMode("no-timestamps");
    setCopyMenuOpen(false);
  };

  // Sort cleaned utterances by start_time (concurrent GPT flushes can arrive out of order)
  // Keep interims at the end so the live rolling text always appears below the cleaned blocks
  const displayUtterances = [
    ...[...liveUtterances]
      .filter(u => u.status === "cleaned")
      .sort((a, b) => (a.start_time ?? 0) - (b.start_time ?? 0)),
    ...liveUtterances.filter(u => u.status === "interim"),
  ];

  const hasLiveContent = liveUtterances.length > 0;
  const hasTranscript =
    transcribedText ||
    (editableTranscriptionRef.current && editableTranscriptionRef.current.length > 0) ||
    hasLiveContent;

  return (
    <div
      className={`relative top-0 right-0 flex min-h-0 flex-1 flex-col transform transition-transform duration-300 ease-in-out
        ${isVisible ? "translate-x-0" : "hidden translate-x-full"}
        ${className}`}
    >
      {/* The surface. Matches the notes box in sections/create-notes/notes-panel.jsx:
          one radius, one hairline, one elevation. It was rounded-ns-tile with a
          2px strong border and `shadow-pop` — an elevation meant for modals, so
          it floated while the notes beside it lay flat.

          Height comes from the parent column, not from the viewport: the fixed
          65vh here was independent of the column's own 65vh minus its header,
          so the two boxes ended at different points. It is flex-1 rather than
          h-full because the column's height is itself flex-derived, and a
          percentage against that collapses to content height below xl. */}
      <div
        className={`rounded-ns group relative bg-white pl-7 pt-7 border border-solid shadow-card border-rule w-full min-h-0 flex-1 flex flex-col
          ${showRecommendation ? "pb-12" : "pb-16"}`}
      >
        <div
          className="flex-1 pr-7 overflow-y-auto transcript-scroll relative [scrollbar-gutter:stable]"
          ref={transcriptScrollRef}
          onScroll={() => {
            const el = transcriptScrollRef.current;
            if (!el) return;
            const { scrollTop, scrollHeight, clientHeight } = el;
            const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
            autoScrollEnabledRef.current = isAtBottom;
            setShowScrollButton(!isAtBottom && isRecording);
          }}
        >
          {/* ── Recommendation view ──────────────────────────────────────── */}
          {showRecommendation && (
            <div className="text-sm outline-none min-h-[20px]">
              {recommendationLoading
                ? "Analysing your session..."
                : recommendation.length > 0
                ? (
                  <TypewriterMarkdown
                    text={recommendation}
                    speed={2}
                    startDelay={400}
                    uniqueId="recommendation"
                    typedNotesRef={typedRecommendationRef}
                  />
                )
                : "Insights will appear here..."}
            </div>
          )}

          {/* ── Ambient utterance list (live + persists after recording) ─── */}
          {!showRecommendation && hasLiveContent && (
            <div className="text-sm">
              {displayUtterances.map(u => (
                <UtteranceItem
                  key={u.id}
                  utterance={u}
                  isEditable={!isRecording}
                  onTextChange={onUtteranceTextChange}
                />
              ))}
              {isRecording && (
                <div className="text-[11px] text-muted italic animate-pulse mt-1">
                  Listening...
                </div>
              )}
            </div>
          )}

          {/* ── Listening placeholder (recording, no utterances yet) ───── */}
          {!showRecommendation && !hasLiveContent && isRecording && (
            <div className="pointer-events-none absolute top-0 left-0 flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent-light opacity-70" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
              </span>
              <span className="font-body text-ui-sm italic text-muted">Listening…</span>
            </div>
          )}

          {/* ── Flat editable transcript (no live utterances — saved sessions) ── */}
          {!showRecommendation && !hasLiveContent && !isRecording && (
            <>
              {/* Empty state, not a placeholder line. "Your transcript will appear
                  here..." said what the box was called; it never said what to do
                  or what would happen. It sits *behind* the editable region and
                  ignores pointer events, so a click anywhere still lands in the
                  text. */}
              {!hasTranscript && !transcriptFocused && (
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent-tint">
                    <Mic size={18} className="text-accent" />
                  </span>
                  <p className="font-body text-ui-sm font-medium text-ink">
                    Your conversation appears here.
                  </p>
                  <p className="max-w-[17rem] font-body text-ui-xs leading-relaxed text-muted">
                    Press Start listening and it writes itself as you talk. You can
                    edit any of it afterwards.
                  </p>
                </div>
              )}
              {/* min-h-full, not min-h-[20px]. The editable region used to be a
                  20px strip at the top of a 500px box, so clicking anywhere below
                  the first line — which is most of the box — put the caret
                  nowhere and the transcript read as uneditable. */}
              <div
                className="relative min-h-full cursor-text text-sm outline-none"
                contentEditable={true}
                suppressContentEditableWarning
                onInput={e => {
                  editableTranscriptionRef.current = e.currentTarget.innerText;
                  // Update appears on a real change, not on a click. It used to be
                  // armed by the box's onClick, so it offered to save an edit
                  // nobody had made.
                  setIsTranscriptEdited(
                    e.currentTarget.innerText.trim() !== baselineTranscript.trim()
                  );
                }}
                onFocus={() => setTranscriptFocused(true)}
                onBlur={() => setTranscriptFocused(false)}
              >
                {baselineTranscript}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Short transcript warning ─────────────────────────────────── */}
      {!showRecommendation && shortTranscriptWarning && (
        <div className="absolute bottom-4 left-[26px] z-[99] flex items-center gap-2 bg-warn/10 border border-warn rounded-ns px-3 py-[7px] text-xs text-warn">
          <AlertTriangle size={13} className="shrink-0 text-warn" />
          <span>Short transcript — review notes carefully</span>
        </div>
      )}

      {/* ── Bottom action buttons: Update + Copy ─────────────────────── */}
      {!showRecommendation && (
        <div className="absolute bottom-4 right-[26px] z-[99] flex items-center gap-2">
          {/* StarsIcon was a raster asset with its own black plate baked in, so it
              sat as a dark square on the accent pill. A stroked lucide glyph
              inherits currentColor and has no ground of its own. */}
          {isTranscriptEdited && (
            <div
              className="cursor-pointer select-none flex items-center gap-1.5 py-[5px] px-[10px] bg-accent rounded-ns font-medium text-white text-sm hover:bg-deep transition-colors duration-ui ease-ui"
              contentEditable={false}
              onMouseDown={() => {
                if (createSessionLoading || loading) return;
                handleTranscriptUpdate();
              }}
            >
              {createSessionLoading || loading ? (
                <Spinner />
              ) : (
                <>
                  <Check size={14} strokeWidth={2.5} />
                  Update
                </>
              )}
            </div>
          )}
          {(hasLiveContent || hasTranscript) && (
            <div ref={copyMenuRef} className="relative">
              {/* Dropdown — opens upward above the button */}
              {copyMenuOpen && (
                <div className="absolute bottom-full right-0 mb-1.5 bg-white border border-rule rounded-ns shadow-pop overflow-hidden z-20 min-w-[200px]">
                  <button
                    onClick={handleCopyWithTimestamps}
                    className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-ink hover:bg-shade-50 transition-colors text-left"
                  >
                    {/* Tick takes the space of the copy icon so text stays aligned */}
                    {copyMode === "timestamps"
                      ? <Check size={13} strokeWidth={2.5} className="shrink-0 text-ink" />
                      : <span className="w-[13px] shrink-0" />
                    }
                    Copy with timestamps
                  </button>
                  <div className="border-t border-rule" />
                  <button
                    onClick={handleCopyWithoutTimestamps}
                    className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-ink hover:bg-shade-50 transition-colors text-left"
                  >
                    {copyMode === "no-timestamps"
                      ? <Check size={13} strokeWidth={2.5} className="shrink-0 text-ink" />
                      : <span className="w-[13px] shrink-0" />
                    }
                    Copy without timestamps
                  </button>
                </div>
              )}

              {/* Split button: left = copy with timestamps (default), right = open menu */}
              <div className="flex items-stretch">
                <button
                  onClick={handleCopyWithTimestamps}
                  className="flex items-center gap-1.5 py-[5px] pl-[10px] pr-2.5 bg-shade-50 rounded-l-2xl font-medium text-ink text-sm transition-colors duration-150 select-none"
                >
                  {copied
                    ? <><Check size={13} strokeWidth={2.5} /><span>Copied</span></>
                    : <><Copy size={13} strokeWidth={2} /><span>Copy</span></>
                  }
                </button>
                <button
                  onClick={() => setCopyMenuOpen(o => !o)}
                  className="flex items-center justify-center py-[5px] px-1.5 bg-shade-50 rounded-r-2xl text-ink transition-colors duration-150 border-l border-accent-light select-none"
                  aria-label="More copy options"
                >
                  <ChevronUp size={12} strokeWidth={2.5} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── AI advisory disclaimer ───────────────────────────────────────── */}
      {showRecommendation && (
        <div className="absolute bottom-2 left-6 text-xs flex items-center gap-3 font-semibold">
          <div>
            <img src={WarnIcon} alt="" width={18} />
          </div>
          <div className="italic font-normal">
            AI-generated insights are advisory only and must be interpreted using clinical judgment
          </div>
        </div>
      )}

      {/* ── Scroll-to-bottom button ──────────────────────────────────────── */}
      {showScrollButton && (
        <button
          onClick={() => {
            const el = transcriptScrollRef.current;
            if (!el) return;
            el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
            autoScrollEnabledRef.current = true;
            setShowScrollButton(false);
          }}
          className="absolute bottom-16 left-1/2 -translate-x-1/2
            bg-accent-light text-ink font-bold px-3 py-1.5 rounded-full text-xs
            flex items-center gap-1 shadow-pop z-10"
        >
          <ArrowDown size={14} />
          New Transcript
        </button>
      )}
    </div>
  );
};
